import { useCallback, useState } from 'react';
import { getIdToken } from '@helios/auth-firebase-web';

/* ------------------------------------------------------------------ */
/*  useSendEmail (PRD.240/241)                                        */
/*                                                                    */
/*  Thin client for the `psendEmail` Cloud Function. The backend      */
/*  owns SendGrid credentials, sender identity, and the actual send.  */
/*  This hook only owns:                                              */
/*    - fetching the Firebase Auth bearer token                       */
/*    - posting the request shape psendEmail expects                  */
/*    - exposing status/error/lastResult for UI feedback              */
/*                                                                    */
/*  No DB writes happen here in v1 (per PRD.240/241 §6).              */
/* ------------------------------------------------------------------ */

const EMAIL_URL = import.meta.env.VITE_EMAIL_NOTIFICATIONS_URL;

export type SendEmailStatus = 'idle' | 'sending' | 'success' | 'error';

export interface SendEmailAddress {
  email: string;
  name?: string;
}

export interface SendEmailInput {
  to: SendEmailAddress[];
  cc?: SendEmailAddress[];
  bcc?: SendEmailAddress[];
  replyTo?: SendEmailAddress;
  subject: string;
  text?: string;
  html?: string;
}

/** Shape returned by `psendEmail` on success (mirrors @helios/email-core). */
export interface SendEmailResult {
  ok: true;
  provider: string;
  providerMessageId?: string;
  statusCode?: number;
  accepted?: string[];
  rejected?: string[];
}

export interface UseSendEmailReturn {
  status: SendEmailStatus;
  error: string | null;
  lastResult: SendEmailResult | null;
  /** True when VITE_EMAIL_NOTIFICATIONS_URL is set. */
  enabled: boolean;
  /** Submit an email. Returns the result, or null on error. */
  send: (input: SendEmailInput) => Promise<SendEmailResult | null>;
  reset: () => void;
}

export function useSendEmail(): UseSendEmailReturn {
  const [status, setStatus] = useState<SendEmailStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<SendEmailResult | null>(null);

  const enabled = Boolean(EMAIL_URL);

  const send = useCallback(async (input: SendEmailInput): Promise<SendEmailResult | null> => {
    if (!EMAIL_URL) {
      setError('Email service is not configured for this clinic.');
      setStatus('error');
      return null;
    }

    setStatus('sending');
    setError(null);

    try {
      // Firebase ID token. The backend `psendEmail` rejects requests
      // without a valid token (401 UNAUTHENTICATED). PRD.240 §12.
      const token = await getIdToken();
      if (!token) {
        setError('You must be signed in to send email.');
        setStatus('error');
        return null;
      }

      const res = await fetch(EMAIL_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(input),
      });

      const body: unknown = await res.json().catch(() => null);

      if (!res.ok) {
        setError(friendlyError(res.status, body));
        setStatus('error');
        return null;
      }

      const result = body as SendEmailResult;
      setLastResult(result);
      setStatus('success');
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Email could not be sent. Please try again.';
      setError(msg);
      setStatus('error');
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setLastResult(null);
  }, []);

  return { status, error, lastResult, enabled, send, reset };
}

/**
 * Normalise the error envelope from `psendEmail` ({ ok: false, error, message }).
 * We surface the server `message` when present, fall back to a generic line
 * otherwise. We never leak the raw provider error code shape.
 */
function friendlyError(httpStatus: number, body: unknown): string {
  if (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string') {
    return body.message;
  }
  if (httpStatus === 401) return 'You must be signed in to send email.';
  if (httpStatus === 400) return 'The email request was rejected. Please check the form.';
  if (httpStatus >= 500) return 'Email could not be sent. Please try again.';
  return `Email could not be sent (HTTP ${httpStatus}).`;
}
